import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import type React from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";

export type HealthStatus =
  | "good"
  | "warning"
  | "critical"
  | "unknown"
  | "never";

export interface HealthIssue {
  message: string;
  severity: "low" | "medium" | "high";
  type: string;
}

export function getStatusColor(status: HealthStatus): string {
  switch (status) {
    case "good":
      return "tw-text-success";
    case "warning":
      return "tw-text-warning";
    case "critical":
    case "never":
      return "tw-text-destructive";
    default:
      return "tw-text-muted-foreground";
  }
}

export function getBarColor(status: HealthStatus): string {
  switch (status) {
    case "good":
      return "tw-bg-success";
    case "warning":
      return "tw-bg-warning";
    case "critical":
      return "tw-bg-destructive";
    default:
      return "tw-bg-muted-foreground";
  }
}

export function StatusIcon({
  status,
}: {
  status: HealthStatus;
}): React.ReactElement | null {
  if (status === "good")
    return <CheckCircle2 className="tw-h-3 tw-w-3 tw-text-success" />;
  if (status === "warning")
    return <AlertTriangle className="tw-h-3 tw-w-3 tw-text-warning" />;
  if (status === "critical" || status === "never")
    return <XCircle className="tw-h-3 tw-w-3 tw-text-destructive" />;
  return null;
}

export function ValueCell({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="tw-w-28 tw-flex tw-items-center tw-justify-start">
      {children}
    </div>
  );
}

export function BatteryBar({
  value,
  status,
}: {
  value: number | null;
  status: HealthStatus;
}): React.ReactElement {
  if (value === null || value === undefined) {
    return (
      <span className="tw-text-xs tw-font-medium tw-text-muted-foreground tw-italic">
        N/A
      </span>
    );
  }
  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      <div className="tw-h-1.5 tw-w-14 tw-bg-muted tw-rounded tw-overflow-hidden tw-flex-shrink-0">
        <div
          className={cn("tw-h-full tw-rounded", getBarColor(status))}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className={cn("tw-text-xs tw-font-medium", getStatusColor(status))}>
        {value}%
      </span>
    </div>
  );
}

export function IssuesList({
  issues,
}: {
  issues: HealthIssue[];
}): React.ReactElement | null {
  if (issues.length === 0) return null;

  return (
    <>
      <div className="tw-border-t tw-border-border" />
      <div className="tw-space-y-2">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <AlertTriangle className="tw-h-3.5 tw-w-3.5 tw-text-amber-500" />
          <span className="tw-text-xs tw-font-semibold tw-text-foreground">
            {issues.length}{" "}
            {issues.length === 1
              ? strings("common.issue")
              : strings("common.issues")}
          </span>
        </div>
        <div className="tw-space-y-1.5">
          {issues.map((issue, i) => (
            <div
              key={`issue-${issue.message.slice(0, 20)}-${i}`}
              className="tw-flex tw-items-start tw-gap-2"
            >
              <span
                className={cn(
                  "tw-w-2 tw-h-2 tw-rounded-full tw-mt-1 tw-flex-shrink-0",
                  issue.severity === "high" && "tw-bg-destructive",
                  issue.severity === "medium" && "tw-bg-warning",
                  issue.severity === "low" && "tw-bg-info"
                )}
              />
              <span className="tw-text-xs tw-text-muted-foreground tw-leading-relaxed">
                {issue.message}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export function HealthyMessage(): React.ReactElement {
  return (
    <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-py-1">
      <CheckCircle2 className="tw-h-4 tw-w-4 tw-text-success" />
      <span className="tw-text-xs tw-font-medium tw-text-success">
        {strings("tooltips.allSystemsHealthy")}
      </span>
    </div>
  );
}
